import React, { useEffect, useRef, useState } from "react";
import * as faceapi from "face-api.js";

const containerStyle = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#000",
  color: "#fff",
  padding: "1.5rem",
  boxSizing: "border-box",
};

const cardStyle = {
  width: "100%",
  maxWidth: 960,
  border: "1px solid rgba(255,255,255,0.08)",
  background:
    "linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))",
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
  borderRadius: 16,
  padding: "1.1rem",
  boxSizing: "border-box",
};

const videoBoxStyle = {
  position: "relative",
  width: "100%",
  background: "#0f0f10",
  borderRadius: 12,
  overflow: "hidden",
  aspectRatio: "16 / 9",
  marginTop: "0.75rem",
};

const videoStyle = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
};

const canvasStyle = {
  position: "absolute",
  top: 0,
  left: 0,
};

const badgeStyle = {
  display: "inline-block",
  padding: "0.25rem 0.5rem",
  border: "1px solid #fff",
  borderRadius: 6,
  marginRight: "0.5rem",
  marginTop: "0.25rem",
  fontFamily: "monospace",
};

// Colors for plots
const emotionColors = {
  angry: "#ff6b6b",
  disgusted: "#2ecc71",
  fearful: "#e67e22",
  happy: "#ffd60a",
  sad: "#b197fc",
  surprised: "#74c0fc",
  neutral: "#ced4da",
};

// Map emotions to circumplex coordinates (valence, arousal) in [-1, 1]
const circumplexMap = {
  angry: { v: -0.8, a: 0.7 },
  disgusted: { v: -0.7, a: 0.2 },
  fearful: { v: -0.8, a: 0.8 },
  happy: { v: 0.9, a: 0.6 },
  sad: { v: -0.8, a: -0.6 },
  surprised: { v: 0.2, a: 0.8 },
  neutral: { v: 0.0, a: 0.0 },
};

// Study-related affect states
const STUDY_STATES = {
  FOCUSED: "focused",
  CONCENTRATED: "concentrated",
  DISTRACTED: "distracted",
  FRUSTRATED: "frustrated",
  CONFUSED: "confused",
  ENGAGED: "engaged",
  BORED: "bored",
  STRESSED: "stressed",
};

// Colors for study states
const studyStateColors = {
  focused: "#2ecc71", // Green - positive focus
  concentrated: "#27ae60", // Darker green - deep concentration
  distracted: "#e74c3c", // Red - negative distraction
  frustrated: "#e67e22", // Orange - frustration
  confused: "#9b59b6", // Purple - confusion
  engaged: "#3498db", // Blue - positive engagement
  bored: "#95a5a6", // Gray - boredom
  stressed: "#c0392b", // Dark red - stress
};

const EMOTIONS = Object.keys(emotionColors);

// Face tracking constants
const FACE_TRACKING_TIMEOUT = 8000; // 8 seconds before considering a face "lost"
const FACE_SIMILARITY_THRESHOLD = 0.7; // similarity score threshold for matching faces
const FACE_POSITION_WEIGHT = 0.3; // weight for position similarity
const FACE_SIZE_WEIGHT = 0.2; // weight for size similarity
const FACE_FEATURES_WEIGHT = 0.5; // weight for facial features similarity

const barsWrapStyle = {
  display: "none",
  gap: 10,
  alignItems: "flex-end",
  height: 120,
  marginTop: 8,
};

const singleBarStyle = {
  width: 18,
  background:
    "linear-gradient(180deg, rgba(255,255,255,0.06), rgba(0,0,0,0.2))",
  borderRadius: 8,
  border: "1px solid #2a2a2a",
  position: "relative",
  overflow: "hidden",
};

const barLabelStyle = {
  marginTop: 6,
  fontSize: 12,
  textAlign: "center",
  opacity: 0.9,
};

const sectionTitleStyle = {
  fontSize: 14,
  letterSpacing: 0.3,
  textTransform: "uppercase",
  color: "#9aa0a6",
  margin: "0 0 6px 2px",
};

const panelStyle = {
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: 12,
  padding: 10,
  boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
};

const kpiWrapStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
  marginBottom: 6,
};

const gridThreeCol = (isNarrow) =>
  isNarrow
    ? { display: "block" }
    : {
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr",
        gap: 16,
        alignItems: "start",
      };

// Loading animation styles
const loadingSpinnerStyle = {
  display: "inline-block",
  width: 16,
  height: 16,
  border: "2px solid rgba(255,255,255,0.3)",
  borderRadius: "50%",
  borderTopColor: "#fff",
  animation: "spin 1s ease-in-out infinite",
  marginRight: 8,
};

const statusBarStyle = {
  position: "sticky",
  top: 0,
  zIndex: 10,
  background: "rgba(0,0,0,0.8)",
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: 8,
  padding: "8px 12px",
  marginBottom: 12,
  fontSize: 14,
  display: "flex",
  alignItems: "center",
  gap: 8,
};

const statusIndicatorStyle = (status) => ({
  width: 8,
  height: 8,
  borderRadius: "50%",
  backgroundColor:
    status === "loading"
      ? "#ffd60a"
      : status === "success"
      ? "#2ecc71"
      : status === "error"
      ? "#ff6b6b"
      : status === "warning"
      ? "#e67e22"
      : "#9aa0a6",
  animation: status === "loading" ? "pulse 1.5s ease-in-out infinite" : "none",
});

// Add CSS animations
const animationStyles = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes statusPulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.1); }
  }
  @keyframes statusGlow {
    0%, 100% { box-shadow: 0 0 0 rgba(46, 204, 113, 0.4); }
    50% { box-shadow: 0 0 8px rgba(46, 204, 113, 0.4); }
  }
`;

function getTopExpression(expressions) {
  if (!expressions) return null;
  let best = null;
  let bestScore = -Infinity;
  Object.entries(expressions).forEach(([name, value]) => {
    if (typeof value === "number" && value > bestScore) {
      bestScore = value;
      best = { name, score: value };
    }
  });
  return best;
}

// Compute Intersection over Union of two boxes {x,y,width,height}
function computeIoU(a, b) {
  const ax2 = a.x + a.width;
  const ay2 = a.y + a.height;
  const bx2 = b.x + b.width;
  const by2 = b.y + b.height;
  const ix1 = Math.max(a.x, b.x);
  const iy1 = Math.max(a.y, b.y);
  const ix2 = Math.min(ax2, bx2);
  const iy2 = Math.min(ay2, by2);
  const iw = Math.max(0, ix2 - ix1);
  const ih = Math.max(0, iy2 - iy1);
  const inter = iw * ih;
  const union = a.width * a.height + b.width * b.height - inter;
  if (union <= 0) return 0;
  return inter / union;
}

function randomNiceColor(seed) {
  const golden = 0.618033988749895;
  let h = (seed * golden) % 1;
  const s = 0.6;
  const v = 0.95;
  // HSV to RGB
  const i = Math.floor(h * 6);
  const f = h * 6 - i;
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);
  let r, g, b;
  switch (i % 6) {
    case 0:
      r = v;
      g = t;
      b = p;
      break;
    case 1:
      r = q;
      g = v;
      b = p;
      break;
    case 2:
      r = p;
      g = v;
      b = t;
      break;
    case 3:
      r = p;
      g = q;
      b = v;
      break;
    case 4:
      r = t;
      g = p;
      b = v;
      break;
    default:
      r = v;
      g = p;
      b = q;
      break;
  }
  const to255 = (x) => Math.round(x * 255);
  return `rgb(${to255(r)}, ${to255(g)}, ${to255(b)})`;
}

// Calculate face similarity based on position, size, and facial features
function calculateFaceSimilarity(face1, face2, landmarks1, landmarks2) {
  // Position similarity (center distance)
  const center1 = {
    x: face1.x + face1.width / 2,
    y: face1.y + face1.height / 2,
  };
  const center2 = {
    x: face2.x + face2.width / 2,
    y: face2.y + face2.height / 2,
  };
  const distance = Math.hypot(center2.x - center1.x, center2.y - center1.y);
  const maxDistance = Math.sqrt(
    face1.width * face1.height + face2.width * face2.height
  );
  const positionSimilarity = Math.max(0, 1 - distance / maxDistance);

  // Size similarity
  const area1 = face1.width * face1.height;
  const area2 = face2.width * face2.height;
  const sizeSimilarity = Math.min(area1, area2) / Math.max(area1, area2);

  // Facial features similarity (using eye and nose positions from landmarks)
  let featuresSimilarity = 0.5; // default neutral similarity
  if (landmarks1 && landmarks2) {
    try {
      // Use eye positions for feature matching
      const eyes1 = landmarks1.getLeftEye ? landmarks1.getLeftEye() : [];
      const eyes2 = landmarks2.getLeftEye ? landmarks2.getLeftEye() : [];
      const rightEyes1 = landmarks1.getRightEye ? landmarks1.getRightEye() : [];
      const rightEyes2 = landmarks2.getRightEye ? landmarks2.getRightEye() : [];

      if (
        eyes1.length > 0 &&
        eyes2.length > 0 &&
        rightEyes1.length > 0 &&
        rightEyes2.length > 0
      ) {
        // Calculate eye distance ratios
        const eyeDist1 = Math.hypot(
          rightEyes1[0].x - eyes1[0].x,
          rightEyes1[0].y - eyes1[0].y
        );
        const eyeDist2 = Math.hypot(
          rightEyes2[0].x - eyes2[0].x,
          rightEyes2[0].y - eyes2[0].y
        );

        // Normalize by face size
        const normEyeDist1 = eyeDist1 / Math.sqrt(area1);
        const normEyeDist2 = eyeDist2 / Math.sqrt(area2);

        // Calculate similarity based on normalized eye distance
        const eyeSimilarity =
          1 -
          Math.abs(normEyeDist1 - normEyeDist2) /
            Math.max(normEyeDist1, normEyeDist2);
        featuresSimilarity = Math.max(0, eyeSimilarity);
      }
    } catch (e) {
      // If landmark processing fails, use default similarity
      featuresSimilarity = 0.5;
    }
  }

  // Weighted combination
  const totalSimilarity =
    positionSimilarity * FACE_POSITION_WEIGHT +
    sizeSimilarity * FACE_SIZE_WEIGHT +
    featuresSimilarity * FACE_FEATURES_WEIGHT;

  return totalSimilarity;
}

const EmotionAnalysis = () => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [mediaStream, setMediaStream] = useState(null);
  const [loadingModels, setLoadingModels] = useState(true);
  const [error, setError] = useState(null);
  const [isStopped, setIsStopped] = useState(false);
  const [expressions, setExpressions] = useState(null);
  const [topExpression, setTopExpression] = useState(null);
  const [studyState, setStudyState] = useState(null);
  const [studyStateHistory, setStudyStateHistory] = useState([]);
  const detectionIntervalRef = useRef(null);
  const smoothedRef = useRef(null);
  const studyStateRef = useRef(null);
  const historyRef = useRef([]); // [{ t, expressions }]
  const studyHistoryRef = useRef([]); // [{ t, studyState }]
  const sparkRef = useRef(null);
  const circumplexRef = useRef(null);
  const studyStateCanvasRef = useRef(null);
  const lastValuesRef = useRef({});
  const [isNarrow, setIsNarrow] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);

  // Heart rate detection state
  const [heartRate, setHeartRate] = useState(null);
  const [heartRateHistory, setHeartRateHistory] = useState([]);
  const [heartRateStatus, setHeartRateStatus] = useState("idle"); // 'idle', 'detecting', 'ready', 'failed'
  const heartRateDataRef = useRef([]);
  const heartRateCanvasRef = useRef(null);
  const lastHeartRateTimeRef = useRef(0);

  // Blood pressure detection state
  const [bloodPressure, setBloodPressure] = useState(null);
  const [bloodPressureHistory, setBloodPressureHistory] = useState([]);
  const [bloodPressureStatus, setBloodPressureStatus] = useState("idle"); // 'idle', 'detecting', 'ready', 'failed'
  const bloodPressureDataRef = useRef([]);
  const bloodPressureCanvasRef = useRef(null);
  const lastBloodPressureTimeRef = useRef(0);

  // Simplified component status tracking - only track what matters
  const [componentStatus, setComponentStatus] = useState({
    faceDetector: "loading", // 'loading', 'ready', 'failed'
    emotionNet: "loading", // 'loading', 'ready', 'failed'
    landmarks: "loading", // 'loading', 'ready', 'failed'
    camera: "idle", // 'idle', 'loading', 'ready', 'failed'
    detection: "idle", // 'idle', 'detecting', 'no-face', 'face-detected'
    heartRate: "idle", // 'idle', 'detecting', 'ready', 'failed'
    bloodPressure: "idle", // 'idle', 'detecting', 'ready', 'failed'
  });

  const MAX_HISTORY = 240; // ~1 minute at 250ms
  const SMOOTHING_ALPHA = 0.25; // 0..1 (lower = smoother)

  // Heart rate detection constants
  const HEART_RATE_SAMPLE_RATE = 4; // fps (matches detection interval of 250ms)
  const HEART_RATE_WINDOW_SIZE = 40; // ~10 seconds at 4fps
  const HEART_RATE_MIN_BPM = 40;
  const HEART_RATE_MAX_BPM = 200;

  // Blood pressure detection constants
  const BP_SAMPLE_RATE = 4; // fps (matches detection interval of 250ms)
  const BP_WINDOW_SIZE = 60; // ~15 seconds at 4fps (need longer window for BP)
  const BP_MIN_SYSTOLIC = 80;
  const BP_MAX_SYSTOLIC = 200;
  const BP_MIN_DIASTOLIC = 50;
  const BP_MAX_DIASTOLIC = 120;

  // Speaking detection constants
  const SPEAK_WINDOW = 12; // ~3s at 250ms
  const SPEAK_OPEN_THRESHOLD = 0.32; // mouth openness ratio
  const SPEAK_MIN_RATIO_FOR_SPEAKING = 0.36; // hysteresis upper bound
  const SPEAK_MIN_ACTIVE_COUNT = 4; // min frames mouth-open in window

  // Speaking trackers with enhanced tracking
  const speakingTrackersRef = useRef({}); // id -> { id, color, history: number[], speaking: bool, lastBox, lastSeen, faceFeatures, trackingScore }
  const nextSpeakingIdRef = useRef(1);
  const [speakingUsers, setSpeakingUsers] = useState([]); // array of tracker ids currently speaking

  const smoothExpressions = (prev, next) => {
    if (!prev) return next;
    const out = {};
    Object.keys(next).forEach((k) => {
      const pv = typeof prev[k] === "number" ? prev[k] : 0;
      const nv = typeof next[k] === "number" ? next[k] : 0;
      out[k] = pv * (1 - SMOOTHING_ALPHA) + nv * SMOOTHING_ALPHA;
    });
    return out;
  };

  const pushHistory = (exps) => {
    const now = Date.now();
    historyRef.current.push({ t: now, expressions: exps });
    if (historyRef.current.length > MAX_HISTORY) {
      historyRef.current.splice(0, historyRef.current.length - MAX_HISTORY);
    }
  };

  const pushStudyHistory = (state) => {
    const now = Date.now();
    studyHistoryRef.current.push({ t: now, studyState: state });
    if (studyHistoryRef.current.length > MAX_HISTORY) {
      studyHistoryRef.current.splice(
        0,
        studyHistoryRef.current.length - MAX_HISTORY
      );
    }
  };

  // Study state detection algorithm
  const detectStudyState = (expressions) => {
    if (!expressions) return null;

    const {
      neutral = 0,
      happy = 0,
      sad = 0,
      angry = 0,
      fearful = 0,
      disgusted = 0,
      surprised = 0,
    } = expressions;

    // Calculate emotional stability (lower variance = more stable)
    const emotionValues = [
      neutral,
      happy,
      sad,
      angry,
      fearful,
      disgusted,
      surprised,
    ];
    const mean =
      emotionValues.reduce((a, b) => a + b, 0) / emotionValues.length;
    const variance =
      emotionValues.reduce((a, b) => a + Math.pow(b - mean, 2), 0) /
      emotionValues.length;
    const stability = 1 - Math.min(variance * 10, 1); // Normalize to 0-1

    // Calculate attention indicators
    const attentionScore = neutral + happy * 0.3; // Neutral + slight positive = focused
    const distractionScore =
      (angry + fearful + disgusted) * 0.8 + surprised * 0.5;
    const engagementScore = happy * 0.6 + surprised * 0.4; // Positive engagement
    const stressScore = (angry + fearful + sad) * 0.7;
    const boredomScore = Math.max(0, neutral - 0.7) * 2; // High neutral = potential boredom

    // Calculate confidence in current state
    const maxEmotion = Math.max(...emotionValues);
    const confidence = maxEmotion > 0.3 ? maxEmotion : 0;

    // Always create metrics object
    const metrics = {
      stability,
      attentionScore,
      distractionScore,
      engagementScore,
      stressScore,
      boredomScore,
    };

    // Determine study state based on combination of factors
    let detectedState = null;
    let stateConfidence = 0;

    // Focused/Concentrated: High neutral, low negative emotions, stable
    if (neutral > 0.6 && stressScore < 0.2 && stability > 0.7) {
      if (neutral > 0.8 && stability > 0.8) {
        detectedState = STUDY_STATES.CONCENTRATED;
        stateConfidence = Math.min(neutral * stability, 1);
      } else {
        detectedState = STUDY_STATES.FOCUSED;
        stateConfidence = Math.min(neutral * stability * 0.8, 1);
      }
    }
    // Engaged: Positive emotions with good stability
    else if (engagementScore > 0.3 && stressScore < 0.3 && stability > 0.6) {
      detectedState = STUDY_STATES.ENGAGED;
      stateConfidence = Math.min(engagementScore * stability, 1);
    }
    // Distracted: High negative emotions or high surprise
    else if (distractionScore > 0.4 || surprised > 0.6) {
      detectedState = STUDY_STATES.DISTRACTED;
      stateConfidence = Math.min(distractionScore, 1);
    }
    // Frustrated: High anger with some stress
    else if (angry > 0.4 && stressScore > 0.3) {
      detectedState = STUDY_STATES.FRUSTRATED;
      stateConfidence = Math.min(angry * 0.8, 1);
    }
    // Confused: High surprise with negative emotions
    else if (surprised > 0.4 && angry + fearful + sad > 0.3) {
      detectedState = STUDY_STATES.CONFUSED;
      stateConfidence = Math.min(surprised * 0.7, 1);
    }
    // Stressed: High negative emotions
    else if (stressScore > 0.4) {
      detectedState = STUDY_STATES.STRESSED;
      stateConfidence = Math.min(stressScore, 1);
    }
    // Bored: Very high neutral with low engagement
    else if (boredomScore > 0.5 && engagementScore < 0.2) {
      detectedState = STUDY_STATES.BORED;
      stateConfidence = Math.min(boredomScore, 1);
    }

    // Always return metrics, but only return state if confidence is high enough
    if (detectedState && stateConfidence > 0.3) {
      return {
        state: detectedState,
        confidence: stateConfidence,
        metrics,
      };
    }

    // Return just metrics when no specific state is detected
    return {
      state: null,
      confidence: 0,
      metrics,
    };
  };

  // Heart rate detection functions
  const extractROI = (canvas, x, y, width, height) => {
    const ctx = canvas.getContext("2d");
    const imageData = ctx.getImageData(x, y, width, height);
    return imageData;
  };

  const calculateAverageColor = (imageData) => {
    const data = imageData.data;
    let r = 0,
      g = 0,
      b = 0;
    const pixelCount = data.length / 4;

    for (let i = 0; i < data.length; i += 4) {
      r += data[i];
      g += data[i + 1];
      b += data[i + 2];
    }

    return {
      r: r / pixelCount,
      g: g / pixelCount,
      b: b / pixelCount,
    };
  };

  const detectHeartRate = (faceBox) => {
    if (!videoRef.current || !canvasRef.current || !faceBox) return;

    try {
      const canvas = canvasRef.current;
      const video = videoRef.current;

      // Create a temporary canvas to capture the current frame
      const tempCanvas = document.createElement("canvas");
      tempCanvas.width = video.videoWidth || video.clientWidth;
      tempCanvas.height = video.videoHeight || video.clientHeight;
      const tempCtx = tempCanvas.getContext("2d");
      tempCtx.drawImage(video, 0, 0);

      // Define ROI (Region of Interest) - forehead area
      const roiWidth = Math.max(20, faceBox.width * 0.3);
      const roiHeight = Math.max(15, faceBox.height * 0.2);
      const roiX = faceBox.x + (faceBox.width - roiWidth) / 2;
      const roiY = faceBox.y + faceBox.height * 0.1; // Upper part of face

      // Extract ROI and calculate average color
      const roi = extractROI(tempCanvas, roiX, roiY, roiWidth, roiHeight);
      const avgColor = calculateAverageColor(roi);

      // Use green channel for heart rate detection (most sensitive to blood volume changes)
      const greenValue = avgColor.g;
      const timestamp = Date.now();

      // Add to heart rate data
      heartRateDataRef.current.push({ timestamp, value: greenValue });

      // Keep only recent data
      if (heartRateDataRef.current.length > HEART_RATE_WINDOW_SIZE) {
        heartRateDataRef.current.shift();
      }

      // Calculate heart rate if we have enough data
      if (heartRateDataRef.current.length >= HEART_RATE_WINDOW_SIZE) {
        const hrResult = calculateBPM(heartRateDataRef.current);
        console.log(
          `Heart rate calculation: ${heartRateDataRef.current.length} samples, Result:`,
          hrResult
        );

        if (
          hrResult &&
          hrResult.bpm >= HEART_RATE_MIN_BPM &&
          hrResult.bpm <= HEART_RATE_MAX_BPM
        ) {
          setHeartRate({
            bpm: hrResult.bpm,
            confidence: hrResult.confidence,
            method: hrResult.method,
          });
          setHeartRateStatus("ready");
          setComponentStatus((prev) => ({ ...prev, heartRate: "ready" }));

          // Update history
          setHeartRateHistory((prev) => {
            const newHistory = [
              ...prev,
              { timestamp, bpm: hrResult.bpm, confidence: hrResult.confidence },
            ];
            return newHistory.slice(-60); // Keep last 60 readings
          });
        } else {
          console.log(
            `BPM ${hrResult?.bpm} outside valid range (${HEART_RATE_MIN_BPM}-${HEART_RATE_MAX_BPM})`
          );
        }
      } else if (heartRateDataRef.current.length > 10) {
        setHeartRateStatus("detecting");
        setComponentStatus((prev) => ({ ...prev, heartRate: "detecting" }));
        console.log(
          `Collecting heart rate data: ${heartRateDataRef.current.length}/${HEART_RATE_WINDOW_SIZE} samples`
        );
      }
    } catch (error) {
      console.warn("Heart rate detection error:", error);
      setHeartRateStatus("failed");
      setComponentStatus((prev) => ({ ...prev, heartRate: "failed" }));
    }
  };

  const detectBloodPressure = (faceBox) => {
    if (!videoRef.current || !canvasRef.current || !faceBox) return;

    try {
      const canvas = canvasRef.current;
      const video = videoRef.current;

      // Create a temporary canvas to capture the current frame
      const tempCanvas = document.createElement("canvas");
      tempCanvas.width = video.videoWidth || video.clientWidth;
      tempCanvas.height = video.videoHeight || video.clientHeight;
      const tempCtx = tempCanvas.getContext("2d");
      tempCtx.drawImage(video, 0, 0);

      // Define multiple ROIs for blood pressure analysis
      // Forehead (primary)
      const foreheadRoi = {
        width: Math.max(20, faceBox.width * 0.3),
        height: Math.max(15, faceBox.height * 0.2),
        x: faceBox.x + (faceBox.width - Math.max(20, faceBox.width * 0.3)) / 2,
        y: faceBox.y + faceBox.height * 0.1,
      };

      // Cheek area (secondary)
      const cheekRoi = {
        width: Math.max(15, faceBox.width * 0.2),
        height: Math.max(12, faceBox.height * 0.15),
        x: faceBox.x + faceBox.width * 0.1,
        y: faceBox.y + faceBox.height * 0.4,
      };

      // Extract ROIs and calculate average colors
      const foreheadRoiData = extractROI(
        tempCanvas,
        foreheadRoi.x,
        foreheadRoi.y,
        foreheadRoi.width,
        foreheadRoi.height
      );
      const cheekRoiData = extractROI(
        tempCanvas,
        cheekRoi.x,
        cheekRoi.y,
        cheekRoi.width,
        cheekRoi.height
      );

      const foreheadColor = calculateAverageColor(foreheadRoiData);
      const cheekColor = calculateAverageColor(cheekRoiData);

      const timestamp = Date.now();

      // Calculate pulse wave features
      const pulseAmplitude = Math.abs(foreheadColor.g - cheekColor.g);
      const pulseIntensity = (foreheadColor.g + cheekColor.g) / 2;
      const pulseVariation = Math.abs(foreheadColor.r - foreheadColor.b); // R-B variation indicates blood oxygenation

      // Add to blood pressure data
      bloodPressureDataRef.current.push({
        timestamp,
        pulseAmplitude,
        pulseIntensity,
        pulseVariation,
        greenValue: foreheadColor.g,
        redValue: foreheadColor.r,
        blueValue: foreheadColor.b,
      });

      // Keep only recent data
      if (bloodPressureDataRef.current.length > BP_WINDOW_SIZE) {
        bloodPressureDataRef.current.shift();
      }

      // Calculate blood pressure if we have enough data
      if (bloodPressureDataRef.current.length >= BP_WINDOW_SIZE) {
        const bp = calculateBloodPressure(bloodPressureDataRef.current);
        console.log(
          `Blood pressure calculation: ${bloodPressureDataRef.current.length} samples, BP: ${bp?.systolic}/${bp?.diastolic}`
        );

        if (
          bp &&
          bp.systolic >= BP_MIN_SYSTOLIC &&
          bp.systolic <= BP_MAX_SYSTOLIC &&
          bp.diastolic >= BP_MIN_DIASTOLIC &&
          bp.diastolic <= BP_MAX_DIASTOLIC
        ) {
          setBloodPressure(bp);
          setBloodPressureStatus("ready");
          setComponentStatus((prev) => ({ ...prev, bloodPressure: "ready" }));

          // Update history
          setBloodPressureHistory((prev) => {
            const newHistory = [...prev, { timestamp, ...bp }];
            return newHistory.slice(-60); // Keep last 60 readings
          });
        } else {
          console.log(
            `BP ${bp?.systolic}/${bp?.diastolic} outside valid range`
          );
        }
      } else if (bloodPressureDataRef.current.length > 15) {
        setBloodPressureStatus("detecting");
        setComponentStatus((prev) => ({ ...prev, bloodPressure: "detecting" }));
        console.log(
          `Collecting blood pressure data: ${bloodPressureDataRef.current.length}/${BP_WINDOW_SIZE} samples`
        );
      }
    } catch (error) {
      console.warn("Blood pressure detection error:", error);
      setBloodPressureStatus("failed");
      setComponentStatus((prev) => ({ ...prev, bloodPressure: "failed" }));
    }
  };

  const calculateBPM = (data) => {
    if (data.length < 20) return null;

    try {
      // Extract values and timestamps
      const values = data.map((d) => d.value);
      const timestamps = data.map((d) => d.timestamp);

      console.log(
        `BPM calculation: ${values.length} values, range: ${Math.min(
          ...values
        ).toFixed(2)}-${Math.max(...values).toFixed(2)}`
      );

      // Apply bandpass filter (0.7-4 Hz, corresponding to 42-240 BPM)
      const filtered = bandpassFilter(values, 0.7, 4, HEART_RATE_SAMPLE_RATE);
      console.log(
        `Filtered signal range: ${Math.min(...filtered).toFixed(2)}-${Math.max(
          ...filtered
        ).toFixed(2)}`
      );

      // Calculate signal quality metrics
      const signalVariance = calculateVariance(filtered);
      const signalRange = Math.max(...filtered) - Math.min(...filtered);
      const signalStrength = Math.sqrt(signalVariance * signalRange);

      // Find peaks using simple peak detection
      const peaks = findPeaks(filtered);
      console.log(`Found ${peaks.length} peaks:`, peaks);

      let bpm = null;
      let confidence = 0;

      if (peaks.length >= 2) {
        // Calculate intervals between peaks
        const intervals = [];
        for (let i = 1; i < peaks.length; i++) {
          const interval = timestamps[peaks[i]] - timestamps[peaks[i - 1]];
          intervals.push(interval);
        }

        console.log(`Peak intervals (ms):`, intervals);

        // Calculate average interval and convert to BPM
        const avgInterval =
          intervals.reduce((a, b) => a + b, 0) / intervals.length;
        bpm = 60000 / avgInterval; // Convert ms to BPM

        // Calculate confidence based on peak consistency and signal quality
        const intervalVariance = calculateVariance(intervals);
        const intervalConsistency =
          1 - Math.min(intervalVariance / (avgInterval * avgInterval), 1);
        const peakConfidence = Math.min(peaks.length / 10, 1); // More peaks = higher confidence
        const signalConfidence = Math.min(signalStrength / 50, 1); // Normalize signal strength

        confidence =
          intervalConsistency * 0.4 +
          peakConfidence * 0.3 +
          signalConfidence * 0.3;

        console.log(
          `Average interval: ${avgInterval.toFixed(0)}ms, BPM: ${bpm.toFixed(
            1
          )}, Confidence: ${confidence.toFixed(2)}`
        );
      } else {
        console.log(
          "Not enough peaks found for BPM calculation, trying FFT method"
        );
        const fftResult = calculateBPMWithFFT(values, timestamps);
        if (fftResult) {
          bpm = fftResult.bpm;
          confidence = fftResult.confidence || 0.3; // Lower confidence for FFT method
        }
      }

      if (bpm) {
        return {
          bpm: Math.round(bpm),
          confidence: Math.min(confidence, 1),
          method: peaks.length >= 2 ? "peak" : "fft",
          signalQuality: {
            variance: signalVariance,
            range: signalRange,
            strength: signalStrength,
          },
        };
      }

      return null;
    } catch (error) {
      console.warn("BPM calculation error:", error);
      return null;
    }
  };

  const bandpassFilter = (signal, lowFreq, highFreq, sampleRate) => {
    // Simple bandpass filter implementation
    const nyquist = sampleRate / 2;
    const low = lowFreq / nyquist;
    const high = highFreq / nyquist;

    // Apply simple moving average as a low-pass filter
    const windowSize = Math.round(sampleRate / (lowFreq * 2));
    const filtered = [];

    for (let i = 0; i < signal.length; i++) {
      let sum = 0;
      let count = 0;

      for (
        let j = Math.max(0, i - windowSize);
        j <= Math.min(signal.length - 1, i + windowSize);
        j++
      ) {
        sum += signal[j];
        count++;
      }

      filtered.push(sum / count);
    }

    return filtered;
  };

  const findPeaks = (signal) => {
    const peaks = [];

    // Calculate dynamic threshold based on signal variance
    const mean = signal.reduce((a, b) => a + b, 0) / signal.length;
    const variance =
      signal.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / signal.length;
    const stdDev = Math.sqrt(variance);
    const threshold = mean + stdDev * 0.5; // More adaptive threshold

    const minDistance = Math.max(2, Math.floor(signal.length / 10)); // Adaptive minimum distance

    console.log(
      `Peak detection: threshold=${threshold.toFixed(
        3
      )}, minDistance=${minDistance}, signal range: ${Math.min(
        ...signal
      ).toFixed(3)}-${Math.max(...signal).toFixed(3)}`
    );

    for (let i = 1; i < signal.length - 1; i++) {
      if (
        signal[i] > signal[i - 1] &&
        signal[i] > signal[i + 1] &&
        signal[i] > threshold
      ) {
        // Check if this peak is far enough from the last one
        if (peaks.length === 0 || i - peaks[peaks.length - 1] > minDistance) {
          peaks.push(i);
        }
      }
    }

    return peaks;
  };

  const calculateBPMWithFFT = (values, timestamps) => {
    try {
      // Simple frequency domain analysis
      const n = values.length;
      const sampleRate = HEART_RATE_SAMPLE_RATE;

      // Calculate time span
      const timeSpan = (timestamps[n - 1] - timestamps[0]) / 1000; // seconds
      const actualSampleRate = n / timeSpan;

      console.log(
        `FFT method: ${n} samples over ${timeSpan.toFixed(
          1
        )}s, actual sample rate: ${actualSampleRate.toFixed(1)} Hz`
      );

      // Simple autocorrelation to find periodicity
      const autocorr = [];
      const maxLag = Math.floor(n / 2);

      for (let lag = 0; lag < maxLag; lag++) {
        let sum = 0;
        let count = 0;

        for (let i = 0; i < n - lag; i++) {
          sum += values[i] * values[i + lag];
          count++;
        }

        autocorr.push(sum / count);
      }

      // Find the peak in autocorrelation (excluding lag 0)
      let maxCorr = 0;
      let bestLag = 0;

      for (let i = 1; i < autocorr.length; i++) {
        if (autocorr[i] > maxCorr) {
          maxCorr = autocorr[i];
          bestLag = i;
        }
      }

      if (bestLag > 0) {
        const period = bestLag / actualSampleRate; // seconds per beat
        const bpm = 60 / period;

        console.log(
          `FFT method: best lag=${bestLag}, period=${period.toFixed(
            2
          )}s, BPM=${bpm.toFixed(1)}`
        );

        if (bpm >= HEART_RATE_MIN_BPM && bpm <= HEART_RATE_MAX_BPM) {
          // Calculate confidence for FFT method based on autocorrelation strength
          const confidence = Math.min(maxCorr * 2, 0.8); // FFT method generally has lower confidence
          return {
            bpm: Math.round(bpm),
            confidence: confidence,
            method: "fft",
          };
        }
      }

      return null;
    } catch (error) {
      console.warn("FFT BPM calculation error:", error);
      return null;
    }
  };

  const calculateBloodPressure = (data) => {
    if (data.length < 30) return null;

    try {
      // Extract pulse wave features
      const pulseAmplitudes = data.map((d) => d.pulseAmplitude);
      const pulseIntensities = data.map((d) => d.pulseIntensity);
      const pulseVariations = data.map((d) => d.pulseVariation);
      const greenValues = data.map((d) => d.greenValue);
      const timestamps = data.map((d) => d.timestamp);

      // Calculate time span
      const timeSpan =
        (timestamps[timestamps.length - 1] - timestamps[0]) / 1000; // seconds
      const actualSampleRate = data.length / timeSpan;

      console.log(
        `BP calculation: ${data.length} samples over ${timeSpan.toFixed(
          1
        )}s, sample rate: ${actualSampleRate.toFixed(1)} Hz`
      );

      // Calculate pulse wave velocity (PWV) approximation
      // Higher PWV generally correlates with higher blood pressure
      const pulseAmplitudeVariance = calculateVariance(pulseAmplitudes);
      const pulseIntensityMean =
        pulseIntensities.reduce((a, b) => a + b, 0) / pulseIntensities.length;
      const pulseVariationMean =
        pulseVariations.reduce((a, b) => a + b, 0) / pulseVariations.length;

      // Calculate pulse wave features
      const pulseWaveFeatures = {
        amplitudeVariance: pulseAmplitudeVariance,
        intensityMean: pulseIntensityMean,
        variationMean: pulseVariationMean,
        signalStrength: Math.sqrt(pulseAmplitudeVariance * pulseIntensityMean),
      };

      // Estimate blood pressure using empirical relationships
      // These are simplified models based on pulse wave analysis research
      const baseSystolic = 120; // mmHg
      const baseDiastolic = 80; // mmHg

      // Pulse amplitude variance affects systolic pressure
      const systolicAdjustment = Math.min(
        Math.max(pulseAmplitudeVariance * 50, -30),
        40
      );
      const systolic = Math.round(baseSystolic + systolicAdjustment);

      // Pulse intensity affects diastolic pressure
      const diastolicAdjustment = Math.min(
        Math.max((pulseIntensityMean - 128) * 0.3, -20),
        25
      );
      const diastolic = Math.round(baseDiastolic + diastolicAdjustment);

      // Apply physiological constraints
      const finalSystolic = Math.max(
        BP_MIN_SYSTOLIC,
        Math.min(BP_MAX_SYSTOLIC, systolic)
      );
      const finalDiastolic = Math.max(
        BP_MIN_DIASTOLIC,
        Math.min(BP_MAX_DIASTOLIC, diastolic)
      );

      // Ensure systolic > diastolic
      const adjustedSystolic = Math.max(finalSystolic, finalDiastolic + 20);
      const adjustedDiastolic = Math.min(finalDiastolic, adjustedSystolic - 20);

      console.log(
        `BP estimation: features=${JSON.stringify(
          pulseWaveFeatures
        )}, raw=${systolic}/${diastolic}, final=${adjustedSystolic}/${adjustedDiastolic}`
      );

      return {
        systolic: adjustedSystolic,
        diastolic: adjustedDiastolic,
        confidence: Math.min(pulseWaveFeatures.signalStrength / 10, 1), // Normalize confidence
        features: pulseWaveFeatures,
      };
    } catch (error) {
      console.warn("Blood pressure calculation error:", error);
      return null;
    }
  };

  const calculateVariance = (values) => {
    const mean = values.reduce((a, b) => a + b, 0) / values.length;
    const variance =
      values.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / values.length;
    return variance;
  };

  const drawHeartRateWaveform = () => {
    const canvas = heartRateCanvasRef.current;
    if (!canvas || heartRateDataRef.current.length < 2) return;

    const ctx = canvas.getContext("2d");
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    // Set up drawing area
    const margin = { top: 20, right: 20, bottom: 40, left: 40 };
    const plotW = w - margin.left - margin.right;
    const plotH = h - margin.top - margin.bottom;

    // Draw axes
    ctx.strokeStyle = "#3a3a3a";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(margin.left, margin.top);
    ctx.lineTo(margin.left, margin.top + plotH);
    ctx.lineTo(margin.left + plotW, margin.top + plotH);
    ctx.stroke();

    // Draw waveform
    const data = heartRateDataRef.current;
    const minVal = Math.min(...data.map((d) => d.value));
    const maxVal = Math.max(...data.map((d) => d.value));
    const range = maxVal - minVal || 1;

    ctx.strokeStyle = "#ff6b6b";
    ctx.lineWidth = 2;
    ctx.beginPath();

    data.forEach((point, i) => {
      const x = margin.left + (i / (data.length - 1)) * plotW;
      const y = margin.top + plotH - ((point.value - minVal) / range) * plotH;

      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });

    ctx.stroke();

    // Draw current heart rate
    if (heartRate) {
      ctx.fillStyle = "#fff";
      ctx.font = "16px system-ui, -apple-system, Segoe UI, Roboto";
      ctx.textAlign = "right";
      ctx.fillText(`${heartRate.bpm} BPM`, w - 10, 20);

      // Draw confidence indicator
      ctx.fillStyle = `rgba(255, 255, 255, ${heartRate.confidence})`;
      ctx.font = "12px system-ui, -apple-system, Segoe UI, Roboto";
      ctx.fillText(
        `Confidence: ${Math.round(heartRate.confidence * 100)}%`,
        w - 10,
        40
      );
    }
  };

  const drawBloodPressureChart = () => {
    const canvas = bloodPressureCanvasRef.current;
    if (!canvas || bloodPressureDataRef.current.length < 2) return;

    const ctx = canvas.getContext("2d");
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    // Set up drawing area
    const margin = { top: 20, right: 20, bottom: 40, left: 40 };
    const plotW = w - margin.left - margin.right;
    const plotH = h - margin.top - margin.bottom;

    // Draw axes
    ctx.strokeStyle = "#3a3a3a";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(margin.left, margin.top);
    ctx.lineTo(margin.left, margin.top + plotH);
    ctx.lineTo(margin.left + plotW, margin.top + plotH);
    ctx.stroke();

    // Draw blood pressure waveform
    const data = bloodPressureDataRef.current;
    const pulseAmplitudes = data.map((d) => d.pulseAmplitude);
    const minVal = Math.min(...pulseAmplitudes);
    const maxVal = Math.max(...pulseAmplitudes);
    const range = maxVal - minVal || 1;

    // Draw pulse amplitude waveform
    ctx.strokeStyle = "#e74c3c";
    ctx.lineWidth = 2;
    ctx.beginPath();

    data.forEach((point, i) => {
      const x = margin.left + (i / (data.length - 1)) * plotW;
      const y =
        margin.top + plotH - ((point.pulseAmplitude - minVal) / range) * plotH;

      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });

    ctx.stroke();

    // Draw current blood pressure
    if (bloodPressure) {
      ctx.fillStyle = "#fff";
      ctx.font = "14px system-ui, -apple-system, Segoe UI, Roboto";
      ctx.textAlign = "right";
      ctx.fillText(
        `${bloodPressure.systolic}/${bloodPressure.diastolic} mmHg`,
        w - 10,
        20
      );

      // Draw confidence indicator
      ctx.fillStyle = `rgba(255, 255, 255, ${bloodPressure.confidence})`;
      ctx.font = "12px system-ui, -apple-system, Segoe UI, Roboto";
      ctx.fillText(
        `Confidence: ${Math.round(bloodPressure.confidence * 100)}%`,
        w - 10,
        40
      );
    }

    // Draw y-axis labels
    ctx.fillStyle = "#aaa";
    ctx.font = "10px system-ui, -apple-system, Segoe UI, Roboto";
    ctx.textAlign = "right";
    ctx.textBaseline = "middle";
    ctx.fillText("Pulse Amplitude", margin.left - 5, margin.top + plotH / 2);
  };

  const drawSparkline = () => {
    const canvas = sparkRef.current;
    const history = historyRef.current;
    if (!canvas || history.length < 2) return;
    const ctx = canvas.getContext("2d");
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    // margins for axes and legend - adjusted for taller canvas
    const m = { top: 50, right: 10, bottom: 22, left: 36 };
    const plotW = w - m.left - m.right;
    const plotH = h - m.top - m.bottom;

    // axes
    ctx.strokeStyle = "#2f2f2f";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(m.left, m.top);
    ctx.lineTo(m.left, m.top + plotH);
    ctx.lineTo(m.left + plotW, m.top + plotH);
    ctx.stroke();

    const keys = Object.keys(emotionColors);
    const len = history.length;
    const xStep = plotW / Math.max(len - 1, 1);

    keys.forEach((key) => {
      ctx.beginPath();
      ctx.strokeStyle = emotionColors[key];
      ctx.lineWidth = 1.5;
      history.forEach((pt, i) => {
        const val = Math.max(0, Math.min(1, pt.expressions[key] || 0));
        const x = m.left + i * xStep;
        const y = m.top + (plotH - val * (plotH - 2)) - 1;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();
    });

    // y-axis ticks and labels
    ctx.fillStyle = "#aaa";
    ctx.font = "12px system-ui, -apple-system, Segoe UI, Roboto";
    [0, 0.5, 1].forEach((t) => {
      const y = m.top + (1 - t) * plotH;
      ctx.globalAlpha = 0.4;
      ctx.beginPath();
      ctx.moveTo(m.left, y);
      ctx.lineTo(m.left + plotW, y);
      ctx.stroke();
      ctx.globalAlpha = 1;
      ctx.fillText(`${Math.round(t * 100)}%`, 6, y + 4);
    });

    // legend
    let lx = m.left + 6;
    let ly = 14; // stays within enlarged top margin
    keys.forEach((k) => {
      ctx.fillStyle = emotionColors[k];
      ctx.fillRect(lx, ly - 7, 16, 3);
      ctx.fillStyle = "#ccc";
      ctx.fillText(k, lx + 22, ly - 2);
      lx += 70;
      if (lx > w - 80) {
        lx = m.left + 6;
        ly += 18;
      }
    });
  };

  const drawCircumplex = () => {
    const canvas = circumplexRef.current;
    if (!canvas || !smoothedRef.current) return;
    const ctx = canvas.getContext("2d");
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    // margins
    const m = { top: 60, right: 60, bottom: 70, left: 70 };
    const plotW = w - m.left - m.right;
    const plotH = h - m.top - m.bottom;

    // Draw axes
    ctx.strokeStyle = "#3a3a3a";
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(m.left, m.top + plotH / 2);
    ctx.lineTo(m.left + plotW, m.top + plotH / 2);
    ctx.moveTo(m.left + plotW / 2, m.top);
    ctx.lineTo(m.left + plotW / 2, m.top + plotH);
    ctx.stroke();

    // Weighted average of circumplex coords
    let sumW = 0;
    let v = 0;
    let a = 0;
    Object.entries(smoothedRef.current).forEach(([k, score]) => {
      const m = circumplexMap[k];
      if (!m) return;
      v += m.v * score;
      a += m.a * score;
      sumW += score;
    });
    if (sumW > 0) {
      v /= sumW;
      a /= sumW;
    }

    // Convert to canvas coords
    const x = m.left + (v + 1) * 0.5 * plotW;
    const y = m.top + (1 - (a + 1) * 0.5) * plotH;

    // Draw dot
    ctx.fillStyle = "#fff";
    ctx.strokeStyle = "#fff";
    ctx.beginPath();
    ctx.arc(x, y, 4, 0, Math.PI * 2);
    ctx.fill();

    // Labels
    ctx.fillStyle = "#d8d8d8";
    ctx.font = "13px system-ui, -apple-system, Segoe UI, Roboto";
    ctx.textBaseline = "middle";
    ctx.textAlign = "center";

    // X-axis ends (valence)
    ctx.fillText("VALENCE (negative)", m.left + 90, m.top + plotH + 30);
    ctx.fillText("VALENCE (positive)", m.left + 90, m.top + plotH - 220);

    // Y-axis ends (arousal)
    ctx.save();
    ctx.translate(m.left - 30, m.top + 80);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText("AROUSAL (low)", 0, 0);
    ctx.restore();
    ctx.save();
    ctx.translate(m.left + plotW + 30, m.top + plotH - 100);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText("AROUSAL (high)", 0, 0);
    ctx.restore();
  };

  const drawStudyStateChart = () => {
    const canvas = studyStateCanvasRef.current;
    if (!canvas || studyHistoryRef.current.length < 2) return;
    const ctx = canvas.getContext("2d");
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    // Set up drawing area with more space for labels
    const margin = { top: 30, right: 20, bottom: 20, left: 60 };
    const plotW = w - margin.left - margin.right;
    const plotH = h - margin.top - margin.bottom;

    // Draw axes
    ctx.strokeStyle = "#3a3a3a";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(margin.left, margin.top);
    ctx.lineTo(margin.left, margin.top + plotH);
    ctx.lineTo(margin.left + plotW, margin.top + plotH);
    ctx.stroke();

    // Draw study state timeline
    const data = studyHistoryRef.current;
    const stateValues = Object.values(STUDY_STATES);
    const stateHeight = plotH / stateValues.length;

    // Draw state regions with better visibility
    data.forEach((point, i) => {
      if (!point.studyState) return;

      const stateIndex = stateValues.indexOf(point.studyState.state);
      if (stateIndex === -1) return;

      const x = margin.left + (i / (data.length - 1)) * plotW;
      const y = margin.top + stateIndex * stateHeight;
      const color = studyStateColors[point.studyState.state] || "#666";

      // Draw thicker bars for better visibility
      ctx.fillStyle = color + "60"; // More opaque
      ctx.fillRect(x - 3, y, 6, stateHeight);
    });

    // Draw current state indicator
    if (studyState) {
      const stateIndex = stateValues.indexOf(studyState.state);
      if (stateIndex !== -1) {
        const x = margin.left + plotW;
        const y = margin.top + stateIndex * stateHeight + stateHeight / 2;
        const color = studyStateColors[studyState.state] || "#666";

        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(x, y, 8, 0, Math.PI * 2);
        ctx.fill();

        // Draw confidence ring
        ctx.strokeStyle = color;
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(x, y, 12 + studyState.confidence * 6, 0, Math.PI * 2);
        ctx.stroke();
      }
    }

    // Draw state labels with better formatting
    ctx.fillStyle = "#d8d8d8";
    ctx.font = "12px system-ui, -apple-system, Segoe UI, Roboto";
    ctx.textBaseline = "middle";
    ctx.textAlign = "right";
    stateValues.forEach((state, i) => {
      const y = margin.top + i * stateHeight + stateHeight / 2;
      const color = studyStateColors[state] || "#666";

      // Draw colored indicator
      ctx.fillStyle = color;
      ctx.fillRect(margin.left - 15, y - 6, 8, 12);

      // Draw state name
      ctx.fillStyle = "#d8d8d8";
      ctx.fillText(
        state.charAt(0).toUpperCase() + state.slice(1),
        margin.left - 20,
        y
      );
    });

    // Draw time axis labels
    ctx.fillStyle = "#aaa";
    ctx.font = "10px system-ui, -apple-system, Segoe UI, Roboto";
    ctx.textAlign = "center";
    ctx.textBaseline = "top";

    // Show time markers (0, 30, 60 seconds ago)
    const timeMarkers = [0, 0.5, 1.0];
    timeMarkers.forEach((timeRatio) => {
      const x = margin.left + timeRatio * plotW;
      const label =
        timeRatio === 0 ? "Now" : `${Math.round(timeRatio * 60)}s ago`;
      ctx.fillText(label, x, margin.top + plotH + 5);
    });

    // Draw current state text at top
    if (studyState) {
      ctx.fillStyle = "#fff";
      ctx.font = "14px system-ui, -apple-system, Segoe UI, Roboto";
      ctx.textAlign = "center";
      ctx.fillText(
        `Current: ${
          studyState.state.charAt(0).toUpperCase() + studyState.state.slice(1)
        } (${Math.round(studyState.confidence * 100)}%)`,
        w / 2,
        10
      );
    }
  };

  useEffect(() => {
    const loadModels = async () => {
      setLoadingModels(true);
      setError(null);
      try {
        const localBases = [
          `${window.location.origin}/models`,
          `${process.env.PUBLIC_URL || ""}/models`,
          "/models",
          "./models",
        ];

        const cdnBases = [
          "https://cdn.jsdelivr.net/npm/@vladmandic/face-api@latest/model",
          "https://raw.githubusercontent.com/justadudewhohacks/face-api.js/master/weights",
        ];

        const basesToTry = [...localBases, ...cdnBases];

        let lastError = null;
        let loaded = false;

        for (const base of basesToTry) {
          try {
            console.log(`Attempting to load models from: ${base}`);

            // Test if models are accessible before loading
            const testUrl = `${base}/tiny_face_detector_model-weights_manifest.json`;
            const testResponse = await fetch(testUrl);
            if (!testResponse.ok) {
              throw new Error(
                `Model manifest not accessible: ${testResponse.status}`
              );
            }

            // Load face detector model
            try {
              await faceapi.nets.tinyFaceDetector.loadFromUri(base);
              if (faceapi.nets.tinyFaceDetector.isLoaded) {
                setComponentStatus((prev) => ({
                  ...prev,
                  faceDetector: "ready",
                }));
                console.log("Face detector model loaded successfully");
              } else {
                throw new Error("Face detector model failed to load");
              }
            } catch (e) {
              console.error("Face detector loading failed:", e.message);
              setComponentStatus((prev) => ({
                ...prev,
                faceDetector: "failed",
              }));
              throw e;
            }

            // Load emotion detection model
            try {
              await faceapi.nets.faceExpressionNet.loadFromUri(base);
              if (faceapi.nets.faceExpressionNet.isLoaded) {
                setComponentStatus((prev) => ({
                  ...prev,
                  emotionNet: "ready",
                }));
                console.log("Emotion detection model loaded successfully");
              } else {
                throw new Error("Emotion detection model failed to load");
              }
            } catch (e) {
              console.error(
                "Emotion detection model loading failed:",
                e.message
              );
              setComponentStatus((prev) => ({ ...prev, emotionNet: "failed" }));
              throw e;
            }

            // Load 68-point landmarks model for mouth geometry
            try {
              await faceapi.nets.faceLandmark68Net.loadFromUri(base);
              if (faceapi.nets.faceLandmark68Net.isLoaded) {
                setComponentStatus((prev) => ({
                  ...prev,
                  landmarks: "ready",
                }));
                console.log("Face landmarks model loaded successfully");
              } else {
                throw new Error("Face landmarks model failed to load");
              }
            } catch (e) {
              console.error("Face landmarks loading failed:", e.message);
              setComponentStatus((prev) => ({ ...prev, landmarks: "failed" }));
              throw e;
            }

            console.log(`Successfully loaded all models from: ${base}`);
            loaded = true;
            break;
          } catch (e) {
            console.warn(`Failed to load from ${base}:`, e.message);
            lastError = e;
          }
        }

        if (!loaded) {
          throw lastError || new Error("All model loading attempts failed");
        }

        // Trigger detection if video is already loaded
        if (videoRef.current && videoRef.current.readyState >= 2) {
          setTimeout(beginDetecting, 100);
        }
      } catch (e) {
        console.error("Model loading error:", e);
        setError(
          `Failed to load face detection models. ${
            e && e.message ? e.message : ""
          }. Please check your internet connection and try refreshing the page.`
        );
      } finally {
        setLoadingModels(false);
      }
    };

    loadModels();
  }, []);

  useEffect(() => {
    const start = async () => {
      setCameraLoading(true);
      setError(null);
      setComponentStatus((prev) => ({ ...prev, camera: "loading" }));
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false,
        });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => {
            if (!canvasRef.current || !videoRef.current || loadingModels)
              return;
            canvasRef.current.width =
              videoRef.current.videoWidth || videoRef.current.clientWidth;
            canvasRef.current.height =
              videoRef.current.videoHeight || videoRef.current.clientHeight;
            setCameraLoading(false);
            setComponentStatus((prev) => ({ ...prev, camera: "ready" }));
            beginDetecting();
          };
        }
        setMediaStream(stream);
      } catch (e) {
        setCameraLoading(false);
        setComponentStatus((prev) => ({ ...prev, camera: "failed" }));
        setError("Unable to access webcam. Please allow camera permissions.");
      }
    };

    if (!mediaStream && !isStopped && !loadingModels) start();

    return () => {
      if (detectionIntervalRef.current) {
        clearInterval(detectionIntervalRef.current);
        detectionIntervalRef.current = null;
      }
      if (mediaStream) mediaStream.getTracks().forEach((t) => t.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaStream, loadingModels]);

  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 900);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Add CSS animations to document head
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = animationStyles;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // Component status item style - larger and clearer
  const componentStatusItemStyle = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontSize: 13,
    padding: "8px 12px",
    borderRadius: 8,
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.12)",
    minHeight: 40,
  };

  // Status indicator style - larger and more visible
  const statusIndicatorStyle = (status) => ({
    width: 12,
    height: 12,
    borderRadius: "50%",
    backgroundColor:
      status === "loading"
        ? "#ffd60a"
        : status === "ready"
        ? "#2ecc71"
        : status === "failed"
        ? "#ff6b6b"
        : status === "no-face"
        ? "#e67e22"
        : status === "face-detected"
        ? "#2ecc71"
        : "#9aa0a6",
    animation:
      status === "loading" ? "pulse 1.5s ease-in-out infinite" : "none",
    border: "2px solid rgba(255,255,255,0.2)",
    boxShadow:
      status === "ready" || status === "face-detected"
        ? "0 0 8px rgba(46, 204, 113, 0.3)"
        : "none",
  });

  // Simplified component status display
  const ComponentStatusGrid = () => {
    const getComponentLabel = (component) => {
      switch (component) {
        case "faceDetector":
          return "Face Detection Model";
        case "emotionNet":
          return "Emotion Analysis Model";
        case "camera":
          return "Camera";
        case "detection":
          return "Face Detection";
        case "heartRate":
          return "Heart Rate Detection";
        case "bloodPressure":
          return "Blood Pressure Detection";
        default:
          return component;
      }
    };

    const getStatusText = (status) => {
      switch (status) {
        case "loading":
          return "Loading...";
        case "ready":
          return "Ready";
        case "failed":
          return "Failed";
        case "idle":
          return "Idle";
        case "detecting":
          return "Detecting...";
        case "face-detected":
          return "Face Found";
        case "no-face":
          return "No Face";
        default:
          return status;
      }
    };

    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isNarrow ? "1fr" : "1fr 1fr",
          gap: 10,
          marginBottom: 12,
        }}
      >
        {Object.entries(componentStatus).map(([component, status]) => (
          <div key={component} style={componentStatusItemStyle}>
            <div style={statusIndicatorStyle(status)} />
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <span style={{ color: "#e5e9ec", fontWeight: 500 }}>
                {getComponentLabel(component)}
              </span>
              <span
                style={{
                  color:
                    status === "failed"
                      ? "#ff6b6b"
                      : status === "ready" || status === "face-detected"
                      ? "#2ecc71"
                      : status === "loading"
                      ? "#ffd60a"
                      : "#9aa0a6",
                  fontSize: 11,
                  fontWeight: 400,
                }}
              >
                {getStatusText(status)}
              </span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Main status bar component - simplified to show only overall system status
  const StatusBar = () => {
    // Determine overall system status
    const hasFailedComponents =
      Object.values(componentStatus).includes("failed");
    const hasLoadingComponents =
      Object.values(componentStatus).includes("loading");
    const allModelsReady =
      componentStatus.faceDetector === "ready" &&
      componentStatus.emotionNet === "ready" &&
      componentStatus.landmarks === "ready";
    const cameraReady = componentStatus.camera === "ready";
    const faceDetected = componentStatus.detection === "face-detected";
    const heartRateReady = componentStatus.heartRate === "ready";
    const bloodPressureReady = componentStatus.bloodPressure === "ready";

    let status = "idle";
    let message = "";
    let showSpinner = false;

    if (error) {
      status = "error";
      message = error;
    } else if (hasFailedComponents) {
      status = "error";
      message = "System Error - Check component status below";
    } else if (hasLoadingComponents) {
      status = "loading";
      message = "Initializing system...";
      showSpinner = true;
    } else if (isStopped || !mediaStream) {
      status = "idle";
      message = "Ready to start - Click 'Start Video' to begin";
    } else if (faceDetected && heartRateReady && bloodPressureReady) {
      status = "success";
      const studyStateText =
        studyState && studyState.state
          ? ` - Study state: ${studyState.state}`
          : "";
      message = `Analyzing emotions, heart rate (${heartRate?.bpm} BPM), and blood pressure (${bloodPressure?.systolic}/${bloodPressure?.diastolic} mmHg) in real-time${studyStateText}`;
    } else if (faceDetected && heartRateReady) {
      status = "success";
      const studyStateText =
        studyState && studyState.state
          ? ` - Study state: ${studyState.state}`
          : "";
      message = `Analyzing emotions and heart rate (${heartRate?.bpm} BPM) in real-time - Blood pressure detection in progress${studyStateText}`;
    } else if (faceDetected) {
      status = "success";
      const studyStateText =
        studyState && studyState.state
          ? ` - Study state: ${studyState.state}`
          : "";
      message = `Analyzing emotions in real-time - Heart rate and blood pressure detection in progress${studyStateText}`;
    } else if (componentStatus.detection === "no-face") {
      status = "warning";
      message = "Position your face in front of the camera";
    } else if (allModelsReady && cameraReady) {
      status = "success";
      message = "System ready - Waiting for face detection";
    } else {
      status = "idle";
      message = "System initializing...";
    }

    return (
      <div>
        <div style={statusBarStyle}>
          <div style={statusIndicatorStyle(status)} />
          {showSpinner && <div style={loadingSpinnerStyle} />}
          <span>{message}</span>
        </div>
        <ComponentStatusGrid />
      </div>
    );
  };

  const beginDetecting = () => {
    if (!videoRef.current || loadingModels) return;
    const options = new faceapi.TinyFaceDetectorOptions({
      inputSize: 320,
      scoreThreshold: 0.5,
    });
    if (detectionIntervalRef.current)
      clearInterval(detectionIntervalRef.current);

    setComponentStatus((prev) => ({ ...prev, detection: "detecting" }));
    detectionIntervalRef.current = setInterval(async () => {
      if (!videoRef.current) return;
      try {
        const results = await faceapi
          .detectAllFaces(videoRef.current, options)
          .withFaceLandmarks()
          .withFaceExpressions();

        const canvas = canvasRef.current;
        if (canvas) {
          const ctx = canvas.getContext("2d");
          ctx.clearRect(0, 0, canvas.width, canvas.height);
        }

        if (results && results.length > 0) {
          setComponentStatus((prev) => ({
            ...prev,
            detection: "face-detected",
          }));
          // For backward-compatibility keep a combined expressions summary from the primary face (largest box)
          const primary = results.reduce(
            (acc, r) =>
              !acc || r.detection.box.area > acc.detection.box.area ? r : acc,
            null
          );
          if (primary) {
            const smoothed = smoothExpressions(
              smoothedRef.current,
              primary.expressions
            );
            smoothedRef.current = smoothed;
            setExpressions(smoothed);
            const best = getTopExpression(primary.expressions);
            setTopExpression(best);
            pushHistory(smoothed);
            lastValuesRef.current = smoothed;

            // Detect study state
            const detectedStudyState = detectStudyState(smoothed);
            if (detectedStudyState) {
              setStudyState(detectedStudyState);
              studyStateRef.current = detectedStudyState;
              // Only push to history if we have a specific state detected
              if (detectedStudyState.state) {
                pushStudyHistory(detectedStudyState);
              }
            }
          }

          drawSparkline();
          drawCircumplex();
          drawStudyStateChart();
          drawHeartRateWaveform();
          drawBloodPressureChart();

          // Drawing, vital signs, and speaking detection per face
          if (canvasRef.current) {
            const dims = {
              width:
                videoRef.current.offsetWidth || videoRef.current.videoWidth,
              height:
                videoRef.current.offsetHeight || videoRef.current.videoHeight,
            };
            faceapi.matchDimensions(canvasRef.current, dims);
            const resizedAll = faceapi.resizeResults(results, dims);
            const ctx = canvasRef.current.getContext("2d");
            ctx.strokeStyle = "#00FF00";
            ctx.lineWidth = 2;

            // Update speaking trackers
            const now = Date.now();
            const updatedSpeaking = new Set();

            resizedAll.forEach((res) => {
              const { x, y, width, height } = res.detection.box;
              ctx.strokeRect(x, y, width, height);

              // Compute mouth openness ratio using landmarks
              const lm = res.landmarks;
              const mouth = lm && lm.getMouth ? lm.getMouth() : [];
              let mouthOpenRatio = 0;
              if (mouth && mouth.length >= 12) {
                // Use inner mouth top (point 62) and bottom (66) approx indices relative to 68-landmark schema
                const top = mouth[13] || mouth[2];
                const bottom = mouth[19] || mouth[6];
                const left = mouth[0];
                const right = mouth[6] || mouth[mouth.length - 1];
                if (top && bottom && left && right) {
                  const vertical = Math.hypot(
                    bottom.x - top.x,
                    bottom.y - top.y
                  );
                  const horizontal = Math.hypot(
                    right.x - left.x,
                    right.y - left.y
                  );
                  const norm = Math.max(1, horizontal);
                  mouthOpenRatio = vertical / norm; // scale-invariant ratio
                }
              }

              // Find matching tracker using improved similarity calculation
              const trackers = speakingTrackersRef.current;
              let matchedId = null;
              let bestSimilarity = 0;
              const currentBox = { x, y, width, height };

              Object.values(trackers).forEach((t) => {
                if (!t.lastBox || !t.faceFeatures) return;

                // Calculate similarity using position, size, and facial features
                const similarity = calculateFaceSimilarity(
                  t.lastBox,
                  currentBox,
                  t.faceFeatures,
                  lm
                );

                if (
                  similarity > bestSimilarity &&
                  similarity > FACE_SIMILARITY_THRESHOLD
                ) {
                  bestSimilarity = similarity;
                  matchedId = t.id;
                }
              });

              if (!matchedId) {
                matchedId = nextSpeakingIdRef.current++;
                trackers[matchedId] = {
                  id: matchedId,
                  color: randomNiceColor(matchedId),
                  history: [],
                  speaking: false,
                  lastBox: null,
                  lastSeen: 0,
                  faceFeatures: null,
                  trackingScore: 0,
                };
              }

              const tr = trackers[matchedId];
              tr.lastBox = { x, y, width, height };
              tr.lastSeen = now;
              tr.faceFeatures = lm; // Store landmarks for future similarity calculations
              tr.trackingScore = bestSimilarity; // Store the similarity score
              tr.history.push(mouthOpenRatio);
              if (tr.history.length > SPEAK_WINDOW) tr.history.shift();

              const avg =
                tr.history.reduce((a, b) => a + b, 0) / tr.history.length;
              const openCount = tr.history.filter(
                (v) => v > SPEAK_OPEN_THRESHOLD
              ).length;
              const isSpeaking =
                avg > SPEAK_MIN_RATIO_FOR_SPEAKING &&
                openCount >= SPEAK_MIN_ACTIVE_COUNT;
              tr.speaking = isSpeaking;
              if (isSpeaking) updatedSpeaking.add(matchedId);

              // Draw label
              const label = `ID ${matchedId}${isSpeaking ? " • speaking" : ""}`;
              ctx.fillStyle = "rgba(0,0,0,0.6)";
              ctx.fillRect(x, Math.max(0, y - 18), Math.min(180, width), 18);
              ctx.fillStyle = tr.color;
              ctx.font = "12px system-ui, -apple-system, Segoe UI, Roboto";
              ctx.fillText(label, x + 6, Math.max(12, y - 5));

              // For vitals, only detect when there's exactly one face to avoid confusion
              if (resizedAll.length === 1) {
                detectHeartRate({ x, y, width, height });
                detectBloodPressure({ x, y, width, height });
              }
            });

            // Cleanup stale trackers and update state list
            const trackers = speakingTrackersRef.current;
            Object.values(trackers).forEach((t) => {
              if (now - t.lastSeen > FACE_TRACKING_TIMEOUT) {
                delete trackers[t.id];
              }
            });
            setSpeakingUsers(Array.from(updatedSpeaking));
          }
        } else {
          setComponentStatus((prev) => ({ ...prev, detection: "no-face" }));
          setExpressions(null);
          setTopExpression(null);
          setStudyState(null);
          studyStateRef.current = null;
          // Reset heart rate and blood pressure when no face is detected
          setHeartRateStatus("idle");
          setComponentStatus((prev) => ({ ...prev, heartRate: "idle" }));
          setBloodPressureStatus("idle");
          setComponentStatus((prev) => ({ ...prev, bloodPressure: "idle" }));
          // Clear speaking list when no faces
          speakingTrackersRef.current = {};
          setSpeakingUsers([]);
        }
      } catch (e) {
        // swallow per-frame errors to avoid spamming
        setComponentStatus((prev) => ({ ...prev, detection: "idle" }));
      }
    }, 250);
  };

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <h2 style={{ margin: 0 }}>
          Live Emotion, Study State, Heart Rate & Blood Pressure Analysis
        </h2>
        <p style={{ marginTop: 8, opacity: 0.9 }}>
          This demo uses your webcam and on-device models to estimate facial
          expressions, detect study-related cognitive states (focused,
          distracted, etc.), and measure heart rate and blood pressure through
          subtle color changes in your skin using photoplethysmography (PPG).
        </p>

        <StatusBar />

        <div style={{ marginTop: "0.75rem" }}>
          {/* Main Layout: 3-column grid for better distribution */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isNarrow ? "1fr" : "1fr 1fr 1fr",
              gap: 16,
              alignItems: "start",
            }}
          >
            {/* Column 1: Camera and Primary Visualizations */}
            <div>
              <div style={{ ...panelStyle, position: "relative" }}>
                <div style={sectionTitleStyle}>Camera Feed</div>
                <div
                  style={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    background:
                      componentStatus.camera === "ready"
                        ? "rgba(46, 204, 113, 0.8)"
                        : componentStatus.camera === "loading"
                        ? "rgba(255, 193, 7, 0.8)"
                        : componentStatus.camera === "failed"
                        ? "rgba(231, 76, 60, 0.8)"
                        : "rgba(108, 117, 125, 0.8)",
                    animation:
                      componentStatus.camera === "loading"
                        ? "statusPulse 2s ease-in-out infinite"
                        : componentStatus.camera === "ready"
                        ? "statusGlow 3s ease-in-out infinite"
                        : "none",
                  }}
                />
                <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                  <button
                    onClick={async () => {
                      if (mediaStream) {
                        // Stop: completely shut down
                        mediaStream
                          .getTracks()
                          .forEach((track) => track.stop());
                        setMediaStream(null);
                        setIsStopped(true);
                        setExpressions(null);
                        setTopExpression(null);
                        setCameraLoading(false);
                        setComponentStatus((prev) => ({
                          ...prev,
                          camera: "idle",
                          detection: "idle",
                        }));
                        // Clear video element
                        if (videoRef.current) {
                          videoRef.current.srcObject = null;
                        }
                        // Stop detection interval
                        if (detectionIntervalRef.current) {
                          clearInterval(detectionIntervalRef.current);
                          detectionIntervalRef.current = null;
                        }
                        // Clear canvas
                        if (canvasRef.current) {
                          const ctx = canvasRef.current.getContext("2d");
                          ctx.clearRect(
                            0,
                            0,
                            canvasRef.current.width,
                            canvasRef.current.height
                          );
                        }
                        // Clear history and charts
                        historyRef.current = [];
                        smoothedRef.current = null;
                        // Clear speaking trackers
                        speakingTrackersRef.current = {};
                        setSpeakingUsers([]);
                        if (sparkRef.current) {
                          const ctx = sparkRef.current.getContext("2d");
                          ctx.clearRect(
                            0,
                            0,
                            sparkRef.current.width,
                            sparkRef.current.height
                          );
                        }
                        if (circumplexRef.current) {
                          const ctx = circumplexRef.current.getContext("2d");
                          ctx.clearRect(
                            0,
                            0,
                            circumplexRef.current.width,
                            circumplexRef.current.height
                          );
                        }
                        // Clear heart rate data
                        heartRateDataRef.current = [];
                        setHeartRate(null);
                        setHeartRateHistory([]);
                        setHeartRateStatus("idle");
                        setComponentStatus((prev) => ({
                          ...prev,
                          heartRate: "idle",
                        }));
                        if (heartRateCanvasRef.current) {
                          const ctx =
                            heartRateCanvasRef.current.getContext("2d");
                          ctx.clearRect(
                            0,
                            0,
                            heartRateCanvasRef.current.width,
                            heartRateCanvasRef.current.height
                          );
                        }
                        // Clear blood pressure data
                        bloodPressureDataRef.current = [];
                        setBloodPressure(null);
                        setBloodPressureHistory([]);
                        setBloodPressureStatus("idle");
                        setComponentStatus((prev) => ({
                          ...prev,
                          bloodPressure: "idle",
                        }));
                        if (bloodPressureCanvasRef.current) {
                          const ctx =
                            bloodPressureCanvasRef.current.getContext("2d");
                          ctx.clearRect(
                            0,
                            0,
                            bloodPressureCanvasRef.current.width,
                            bloodPressureCanvasRef.current.height
                          );
                        }
                        // Clear study state data
                        studyHistoryRef.current = [];
                        setStudyState(null);
                        setStudyStateHistory([]);
                        studyStateRef.current = null;
                        if (studyStateCanvasRef.current) {
                          const ctx =
                            studyStateCanvasRef.current.getContext("2d");
                          ctx.clearRect(
                            0,
                            0,
                            studyStateCanvasRef.current.width,
                            studyStateCanvasRef.current.height
                          );
                        }
                      } else {
                        // Start: get new stream
                        setCameraLoading(true);
                        setError(null);
                        setComponentStatus((prev) => ({
                          ...prev,
                          camera: "loading",
                        }));
                        try {
                          const stream =
                            await navigator.mediaDevices.getUserMedia({
                              video: true,
                              audio: false,
                            });
                          if (videoRef.current) {
                            videoRef.current.srcObject = stream;
                            videoRef.current.onloadedmetadata = () => {
                              if (!canvasRef.current || !videoRef.current)
                                return;
                              canvasRef.current.width =
                                videoRef.current.videoWidth ||
                                videoRef.current.clientWidth;
                              canvasRef.current.height =
                                videoRef.current.videoHeight ||
                                videoRef.current.clientHeight;
                              setCameraLoading(false);
                              setComponentStatus((prev) => ({
                                ...prev,
                                camera: "ready",
                              }));
                              beginDetecting();
                            };
                          }
                          setMediaStream(stream);
                          setIsStopped(false);
                        } catch (e) {
                          setCameraLoading(false);
                          setComponentStatus((prev) => ({
                            ...prev,
                            camera: "failed",
                          }));
                          setError(
                            "Unable to access webcam. Please allow camera permissions."
                          );
                        }
                      }
                    }}
                    style={{
                      background: mediaStream ? "#ff6b6b" : "#4CAF50",
                      color: "white",
                      border: "none",
                      borderRadius: 6,
                      padding: "6px 12px",
                      fontSize: 12,
                      cursor: cameraLoading ? "not-allowed" : "pointer",
                      opacity: cameraLoading ? 0.7 : 1,
                    }}
                    disabled={cameraLoading}
                  >
                    {cameraLoading
                      ? "Starting..."
                      : mediaStream
                      ? "Stop Video"
                      : "Start Video"}
                  </button>
                </div>
                <div style={videoBoxStyle}>
                  <video
                    ref={videoRef}
                    autoPlay
                    muted
                    playsInline
                    style={videoStyle}
                  />
                  <canvas ref={canvasRef} style={canvasStyle} />
                </div>
                {/* Active Speakers */}
                <div style={{ ...panelStyle, marginTop: 12 }}>
                  <div style={sectionTitleStyle}>Active Speakers</div>
                  {speakingUsers.length === 0 ? (
                    <div style={{ color: "#888", fontSize: 12 }}>
                      No one speaking
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {speakingUsers.map((id) => {
                        const tr = speakingTrackersRef.current[id];
                        const color = tr ? tr.color : "#ddd";
                        return (
                          <span
                            key={id}
                            style={{ ...badgeStyle, borderColor: color, color }}
                          >
                            User {id}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Emotion Time Series */}
              <div
                style={{ ...panelStyle, marginTop: 12, position: "relative" }}
              >
                <div style={sectionTitleStyle}>Emotion Timeline</div>
                <div
                  style={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    background:
                      historyRef.current.length >= 2
                        ? "rgba(46, 204, 113, 0.8)"
                        : expressions
                        ? "rgba(255, 193, 7, 0.8)"
                        : "rgba(108, 117, 125, 0.8)",
                    animation:
                      expressions && historyRef.current.length < 2
                        ? "statusPulse 2s ease-in-out infinite"
                        : historyRef.current.length >= 2
                        ? "statusGlow 3s ease-in-out infinite"
                        : "none",
                  }}
                />
                <div style={{ position: "relative" }}>
                  <canvas
                    ref={sparkRef}
                    width={isNarrow ? 340 : 280}
                    height={200}
                    style={{
                      background: "#111",
                      borderRadius: 10,
                      border: "1px solid #333",
                    }}
                  />
                  {historyRef.current.length < 2 && (
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        color: "#666",
                        fontSize: 14,
                        textAlign: "center",
                        pointerEvents: "none",
                      }}
                    >
                      <div>
                        {expressions
                          ? "Building timeline..."
                          : "No emotion data"}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Column 2: Vital Signs and Study State */}
            <div>
              {/* Heart Rate */}
              <div style={{ ...panelStyle, position: "relative" }}>
                <div style={sectionTitleStyle}>Heart Rate</div>
                <div
                  style={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    background:
                      heartRateStatus === "ready"
                        ? "rgba(46, 204, 113, 0.8)"
                        : heartRateStatus === "detecting"
                        ? "rgba(255, 193, 7, 0.8)"
                        : heartRateStatus === "failed"
                        ? "rgba(231, 76, 60, 0.8)"
                        : "rgba(108, 117, 125, 0.8)",
                    animation:
                      heartRateStatus === "detecting"
                        ? "statusPulse 2s ease-in-out infinite"
                        : heartRateStatus === "ready"
                        ? "statusGlow 3s ease-in-out infinite"
                        : "none",
                  }}
                />
                <div style={{ marginBottom: 8 }}>
                  <div
                    style={{
                      fontSize: 18,
                      fontWeight: 600,
                      color: heartRate ? "#ff6b6b" : "#666",
                    }}
                  >
                    {heartRate
                      ? `${heartRate.bpm} BPM`
                      : heartRateStatus === "detecting"
                      ? "Detecting..."
                      : heartRateStatus === "idle"
                      ? "No Data"
                      : "Initializing..."}
                  </div>
                  <div style={{ fontSize: 12, opacity: 0.7 }}>
                    {heartRate ? (
                      <>Confidence: {Math.round(heartRate.confidence * 100)}%</>
                    ) : heartRateStatus === "detecting" ? (
                      "Collecting pulse data..."
                    ) : heartRateStatus === "idle" ? (
                      "Position face in camera view"
                    ) : (
                      "Starting heart rate detection..."
                    )}
                  </div>
                </div>
                <canvas
                  ref={heartRateCanvasRef}
                  width={isNarrow ? 280 : 240}
                  height={80}
                  style={{
                    background: "#111",
                    borderRadius: 10,
                    border: "1px solid #333",
                  }}
                />
              </div>

              {/* Blood Pressure */}
              <div
                style={{ ...panelStyle, marginTop: 12, position: "relative" }}
              >
                <div style={sectionTitleStyle}>Blood Pressure</div>
                <div
                  style={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    background:
                      bloodPressureStatus === "ready"
                        ? "rgba(46, 204, 113, 0.8)"
                        : bloodPressureStatus === "detecting"
                        ? "rgba(255, 193, 7, 0.8)"
                        : bloodPressureStatus === "failed"
                        ? "rgba(231, 76, 60, 0.8)"
                        : "rgba(108, 117, 125, 0.8)",
                    animation:
                      bloodPressureStatus === "detecting"
                        ? "statusPulse 2s ease-in-out infinite"
                        : bloodPressureStatus === "ready"
                        ? "statusGlow 3s ease-in-out infinite"
                        : "none",
                  }}
                />
                <div style={{ marginBottom: 8 }}>
                  <div
                    style={{
                      fontSize: 18,
                      fontWeight: 600,
                      color: bloodPressure ? "#e74c3c" : "#666",
                    }}
                  >
                    {bloodPressure
                      ? `${bloodPressure.systolic}/${bloodPressure.diastolic} mmHg`
                      : bloodPressureStatus === "detecting"
                      ? "Detecting..."
                      : bloodPressureStatus === "idle"
                      ? "No Data"
                      : "Initializing..."}
                  </div>
                  <div style={{ fontSize: 12, opacity: 0.7 }}>
                    {bloodPressure ? (
                      <>
                        Confidence: {Math.round(bloodPressure.confidence * 100)}
                        %
                      </>
                    ) : bloodPressureStatus === "detecting" ? (
                      "Analyzing pulse patterns..."
                    ) : bloodPressureStatus === "idle" ? (
                      "Position face in camera view"
                    ) : (
                      "Starting blood pressure detection..."
                    )}
                  </div>
                </div>
                <canvas
                  ref={bloodPressureCanvasRef}
                  width={isNarrow ? 280 : 240}
                  height={80}
                  style={{
                    background: "#111",
                    borderRadius: 10,
                    border: "1px solid #333",
                  }}
                />
              </div>

              {/* Study State */}
              <div
                style={{ ...panelStyle, marginTop: 12, position: "relative" }}
              >
                <div style={sectionTitleStyle}>Study State</div>
                <div
                  style={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    background:
                      studyState && studyState.state
                        ? "rgba(46, 204, 113, 0.8)"
                        : expressions
                        ? "rgba(255, 193, 7, 0.8)"
                        : "rgba(108, 117, 125, 0.8)",
                    animation:
                      expressions && !studyState?.state
                        ? "statusPulse 2s ease-in-out infinite"
                        : studyState && studyState.state
                        ? "statusGlow 3s ease-in-out infinite"
                        : "none",
                  }}
                />

                {/* Current Study State */}
                <div style={{ marginBottom: 12 }}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      marginBottom: 4,
                      color: "#e5e9ec",
                    }}
                  >
                    Current State
                  </div>
                  <div style={{ fontSize: 16, color: "#fff" }}>
                    {studyState && studyState.state ? (
                      <span
                        style={{ color: studyStateColors[studyState.state] }}
                      >
                        {studyState.state.charAt(0).toUpperCase() +
                          studyState.state.slice(1)}
                        <span style={{ opacity: 0.8, fontSize: 14 }}>
                          {" "}
                          ({Math.round(studyState.confidence * 100)}%)
                        </span>
                      </span>
                    ) : (
                      <span style={{ color: "#666" }}>
                        {expressions ? "Analyzing..." : "No Data"}
                      </span>
                    )}
                  </div>
                </div>

                {/* Study State Metrics */}
                <div style={{ marginBottom: 12 }}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      marginBottom: 4,
                      color: "#e5e9ec",
                    }}
                  >
                    Study Metrics
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 8,
                      fontSize: 12,
                    }}
                  >
                    <div>
                      <span style={{ opacity: 0.7 }}>Stability:</span>
                      <div
                        style={{
                          color: studyState?.metrics ? "#2ecc71" : "#666",
                        }}
                      >
                        {studyState?.metrics
                          ? Math.round(studyState.metrics.stability * 100)
                          : "—"}
                        %
                      </div>
                    </div>
                    <div>
                      <span style={{ opacity: 0.7 }}>Attention:</span>
                      <div
                        style={{
                          color: studyState?.metrics ? "#3498db" : "#666",
                        }}
                      >
                        {studyState?.metrics
                          ? Math.round(studyState.metrics.attentionScore * 100)
                          : "—"}
                        %
                      </div>
                    </div>
                    <div>
                      <span style={{ opacity: 0.7 }}>Engagement:</span>
                      <div
                        style={{
                          color: studyState?.metrics ? "#f39c12" : "#666",
                        }}
                      >
                        {studyState?.metrics
                          ? Math.round(studyState.metrics.engagementScore * 100)
                          : "—"}
                        %
                      </div>
                    </div>
                    <div>
                      <span style={{ opacity: 0.7 }}>Distraction:</span>
                      <div
                        style={{
                          color: studyState?.metrics ? "#e74c3c" : "#666",
                        }}
                      >
                        {studyState?.metrics
                          ? Math.round(
                              studyState.metrics.distractionScore * 100
                            )
                          : "—"}
                        %
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Column 3: Current Analysis and Emotion Breakdown */}
            <div>
              {/* Affect Circumplex */}
              <div style={{ ...panelStyle, position: "relative" }}>
                <div style={sectionTitleStyle}>Affect Circumplex</div>
                <div
                  style={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    background: smoothedRef.current
                      ? "rgba(46, 204, 113, 0.8)"
                      : expressions
                      ? "rgba(255, 193, 7, 0.8)"
                      : "rgba(108, 117, 125, 0.8)",
                    animation:
                      expressions && !smoothedRef.current
                        ? "statusPulse 2s ease-in-out infinite"
                        : smoothedRef.current
                        ? "statusGlow 3s ease-in-out infinite"
                        : "none",
                  }}
                />
                <div style={{ position: "relative" }}>
                  <canvas
                    ref={circumplexRef}
                    width={isNarrow ? 280 : 240}
                    height={isNarrow ? 280 : 240}
                    style={{
                      background: "#111",
                      borderRadius: 10,
                      border: "1px solid #333",
                    }}
                  />
                  {!smoothedRef.current && (
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        color: "#666",
                        fontSize: 14,
                        textAlign: "center",
                        pointerEvents: "none",
                      }}
                    >
                      <div>
                        {expressions
                          ? "Calculating affect..."
                          : "No affect data"}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Current Emotion Analysis */}
              <div
                style={{ ...panelStyle, marginTop: 12, position: "relative" }}
              >
                <div style={sectionTitleStyle}>Current Analysis</div>
                <div
                  style={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    background: topExpression
                      ? "rgba(46, 204, 113, 0.8)"
                      : expressions
                      ? "rgba(255, 193, 7, 0.8)"
                      : "rgba(108, 117, 125, 0.8)",
                    animation:
                      expressions && !topExpression
                        ? "statusPulse 2s ease-in-out infinite"
                        : topExpression
                        ? "statusGlow 3s ease-in-out infinite"
                        : "none",
                  }}
                />

                {/* Top Emotion */}
                <div style={{ marginBottom: 12 }}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      marginBottom: 4,
                      color: "#e5e9ec",
                    }}
                  >
                    Dominant Emotion
                  </div>
                  <div style={{ fontSize: 16, color: "#fff" }}>
                    {topExpression ? (
                      <span
                        style={{ color: emotionColors[topExpression.name] }}
                      >
                        {topExpression.name.charAt(0).toUpperCase() +
                          topExpression.name.slice(1)}
                        <span style={{ opacity: 0.8, fontSize: 14 }}>
                          {" "}
                          ({Math.round(topExpression.score * 100)}%)
                        </span>
                      </span>
                    ) : (
                      <span style={{ color: "#666" }}>
                        {expressions ? "Processing..." : "No Data"}
                      </span>
                    )}
                  </div>
                </div>

                {/* Emotion Breakdown */}
                <div>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      marginBottom: 8,
                      color: "#e5e9ec",
                    }}
                  >
                    Emotion Breakdown
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                    }}
                  >
                    {EMOTIONS.map((name) => {
                      const value = expressions ? expressions[name] || 0 : 0;
                      const pct = Math.max(0, Math.min(1, value));
                      const hasData =
                        expressions && expressions[name] !== undefined;
                      return (
                        <div
                          key={name}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <div
                            style={{
                              width: 70,
                              textAlign: "right",
                              fontSize: 11,
                              color: "#cbd1d6",
                            }}
                          >
                            {name.charAt(0).toUpperCase() + name.slice(1)}
                          </div>
                          <div
                            style={{
                              flex: 1,
                              height: 6,
                              background: "rgba(255,255,255,0.06)",
                              borderRadius: 999,
                              overflow: "hidden",
                            }}
                          >
                            <div
                              style={{
                                width: `${pct * 100}%`,
                                height: "100%",
                                background: hasData
                                  ? emotionColors[name]
                                  : "#666",
                                transition: "width 180ms ease",
                              }}
                            />
                          </div>
                          <div
                            style={{
                              width: 30,
                              textAlign: "left",
                              fontSize: 11,
                              color: hasData ? "#e5e9ec" : "#666",
                            }}
                          >
                            {hasData ? Math.round(pct * 100) : "—"}%
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmotionAnalysis;
